import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { colors } from "../styles/colors";
import { typography } from "../styles/typography";
import { globalStyles } from "../styles/globalStyles";
import bookingAPI from "../services/bookingApi";
import { useUser } from "../context/UserContext";

// Responsive helper functions (adapted for web)
const getResponsiveValue = (small, medium, large) => {
  const width = window.innerWidth;
  if (width >= 1024) return large;
  if (width >= 768) return medium;
  return small;
};

const ManageAdminsPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: userLoading } = useUser();
  
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [newAdmin, setNewAdmin] = useState({
    name: '',
    email: '',
    role: 'Administrator',
    password: ''
  });

  useEffect(() => {
    if (!userLoading && (!isAuthenticated || !user?.is_admin)) {
      alert('Please log in as an administrator to access this page.');
      navigate('/');
      return;
    }
    if (!userLoading && isAuthenticated && user?.is_admin) {
      loadAdmins();
    }
  }, [isAuthenticated, userLoading, user, navigate]);

  const loadAdmins = async () => {
    try {
      setLoading(true);
      const response = await bookingAPI.getAllAdmins();
      if (response.success) {
        setAdmins(response.data || []);
      } else {
        console.error('Failed to load admins:', response.error);
      }
    } catch (error) {
      console.error('Error loading admins:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdmin = () => {
    setNewAdmin({ name: '', email: '', role: 'Administrator', password: '' });
    setSelectedAdmin(null);
    setModalVisible(true);
  };

  const handleEditAdmin = (admin) => {
    setSelectedAdmin(admin);
    setNewAdmin({
      name: admin.name,
      email: admin.email,
      role: admin.role,
      password: ''
    });
    setModalVisible(true);
  };

  const handleSaveAdmin = async () => {
    if (!newAdmin.name || !newAdmin.email || (!selectedAdmin && !newAdmin.password)) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      let response;
      
      if (selectedAdmin) {
        // Update existing admin
        response = await bookingAPI.updateAdmin(selectedAdmin.id, {
          name: newAdmin.name,
          email: newAdmin.email,
          role: newAdmin.role
        });
        if (response.success) {
          alert('Admin updated successfully');
          await loadAdmins();
        } else {
          alert('Failed to update admin: ' + (response.error || 'Unknown error'));
        }
      } else {
        // Add new admin
        response = await bookingAPI.createAdmin({
          name: newAdmin.name,
          email: newAdmin.email,
          role: newAdmin.role,
          password: newAdmin.password
        });
        if (response.success) {
          alert('Admin added successfully');
          await loadAdmins();
        } else {
          alert('Failed to add admin: ' + (response.error || 'Unknown error'));
        }
      }

      setModalVisible(false);
      setSelectedAdmin(null);
    } catch (error) {
      console.error('Error saving admin:', error);
      alert('Error saving admin. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAdmin = async (adminId) => {
    if (window.confirm('Are you sure you want to delete this admin?')) {
      try {
        setLoading(true);
        const response = await bookingAPI.deleteAdmin(adminId);
        if (response.success) {
          alert('Admin deleted successfully');
          await loadAdmins();
        } else {
          alert('Failed to delete admin: ' + (response.error || 'Unknown error'));
        }
      } catch (error) {
        console.error('Error deleting admin:', error);
        alert('Error deleting admin. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const toggleAdminStatus = async (adminId) => {
    try {
      setLoading(true);
      const admin = admins.find(a => a.id === adminId);
      const newStatus = admin.status === 'active' ? 'inactive' : 'active';
      
      const response = await bookingAPI.toggleAdminStatus(adminId, newStatus);
      if (response.success) {
        await loadAdmins();
      } else {
        alert('Failed to update admin status: ' + (response.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error updating admin status:', error);
      alert('Error updating admin status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (error) {
      return dateStr;
    }
  };

  const getStatusColor = (status) => {
    return status === 'active' ? colors.success : colors.gray;
  };

  // Show loading while checking authentication
  if (userLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <div style={styles.loadingText}>Verifying admin access...</div>
        </div>
      </div>
    );
  }

  if (loading && admins.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <div style={styles.loadingText}>Loading admin management...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.scrollView}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <div>
              <div style={styles.title}>👥 Manage Admins</div>
              <div style={styles.subtitle}>
                Add, edit, or remove administrator accounts ({admins.length} admins)
              </div>
            </div>
            <button style={styles.backButton} onClick={() => navigate('/admin')}>
              <span style={styles.backButtonText}>← Back to Dashboard</span>
            </button>
          </div>
        </div>

        {/* Add Admin Button */}
        <div style={styles.actionSection}>
          <button style={styles.addButton} onClick={handleAddAdmin}>
            <span style={styles.addButtonText}>➕ Add New Admin</span>
          </button>
        </div>

        {/* Admins List */}
        <div style={styles.adminsContainer}>
          {admins.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>👥</div>
              <div style={styles.emptyTitle}>No admins found</div>
              <div style={styles.emptySubtitle}>Add your first administrator</div>
            </div>
          ) : (
            <div style={styles.adminsGrid}>
              {admins.map((admin) => (
                <div key={admin.id} style={styles.adminCard}>
                  <div style={styles.cardHeader}>
                    <div style={styles.adminInfo}>
                      <div style={styles.adminName}>{admin.name}</div>
                      <div style={styles.adminEmail}>{admin.email}</div>
                      <div style={styles.adminRole}>{admin.role}</div>
                    </div>
                    <div style={{
                      ...styles.statusBadge,
                      backgroundColor: getStatusColor(admin.status)
                    }}>
                      <span style={styles.statusText}>{admin.status}</span>
                    </div>
                  </div>

                  <div style={styles.adminDetails}>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Created:</span>
                      <span style={styles.detailValue}>{formatDate(admin.createdAt)}</span>
                    </div>
                  </div>

                  <div style={styles.cardActions}>
                    <button
                      style={styles.editButton}
                      onClick={() => handleEditAdmin(admin)}
                    >
                      <span style={styles.editButtonText}>✏️ Edit</span>
                    </button>
                    <button
                      style={styles.statusButton}
                      onClick={() => toggleAdminStatus(admin.id)}
                    >
                      <span style={styles.statusButtonText}>
                        {admin.status === 'active' ? '🔒 Deactivate' : '🔓 Activate'}
                      </span>
                    </button>
                    <button
                      style={styles.deleteButton}
                      onClick={() => handleDeleteAdmin(admin.id)}
                    >
                      <span style={styles.deleteButtonText}>🗑️ Delete</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Admin Modal */}
      {modalVisible && (
        <div style={styles.modalOverlay} onClick={() => setModalVisible(false)}>
          <div style={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>
                {selectedAdmin ? 'Edit Admin' : 'Add New Admin'}
              </div>
              <button style={styles.closeButton} onClick={() => setModalVisible(false)}>×</button>
            </div>
            
            <div style={styles.modalContent}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Name *</label>
                <input 
                  type="text"
                  style={styles.input}
                  value={newAdmin.name}
                  onChange={(e) => setNewAdmin({...newAdmin, name: e.target.value})}
                  placeholder="Enter admin name"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Email *</label>
                <input 
                  type="email"
                  style={styles.input}
                  value={newAdmin.email}
                  onChange={(e) => setNewAdmin({...newAdmin, email: e.target.value})}
                  placeholder="Enter email address"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Role *</label>
                <select 
                  style={styles.select}
                  value={newAdmin.role}
                  onChange={(e) => setNewAdmin({...newAdmin, role: e.target.value})}
                >
                  <option value="Administrator">Administrator</option>
                  <option value="Super Admin">Super Admin</option>
                  <option value="Moderator">Moderator</option>
                </select>
              </div>

              {!selectedAdmin && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Password *</label>
                  <input 
                    type="password"
                    style={styles.input}
                    value={newAdmin.password}
                    onChange={(e) => setNewAdmin({...newAdmin, password: e.target.value})}
                    placeholder="Enter password"
                  />
                </div>
              )}

              {selectedAdmin && (
                <div style={styles.noteText}>
                  Note: Leave password field empty to keep current password
                </div>
              )}
            </div>
            
            <div style={styles.modalActions}>
              <button style={styles.cancelButton} onClick={() => setModalVisible(false)}>
                Cancel
              </button>
              <button style={styles.saveButton} onClick={handleSaveAdmin}>
                {selectedAdmin ? 'Update Admin' : 'Add Admin'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    flex: 1,
    backgroundColor: colors.background,
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  scrollView: {
    flex: 1,
    overflowY: 'auto',
  },
  loadingContainer: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  spinner: {
    width: 50,
    height: 50,
    border: `4px solid ${colors.border}`,
    borderTop: `4px solid ${colors.primary}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    ...typography.body,
    color: colors.textLight,
    marginTop: 16,
  },
  header: {
    backgroundColor: colors.primary,
    padding: getResponsiveValue(20, 24, 32),
  },
  headerContent: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontSize: getResponsiveValue(24, 28, 32),
    fontWeight: 'bold',
    color: colors.white,
    marginBottom: 8,
  },
  subtitle: {
    ...typography.body,
    color: colors.white,
    opacity: 0.9,
  },
  backButton: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    padding: getResponsiveValue(8, 10, 12),
    borderRadius: getResponsiveValue(6, 8, 10),
    border: 'none',
    cursor: 'pointer',
  },
  backButtonText: {
    color: colors.white,
    fontSize: getResponsiveValue(14, 16, 18),
    fontWeight: '600',
  },
  actionSection: {
    padding: getResponsiveValue(16, 20, 32),
    paddingBottom: 0,
  },
  addButton: {
    backgroundColor: colors.success,
    padding: getResponsiveValue(12, 16, 20),
    borderRadius: getResponsiveValue(8, 10, 12),
    border: 'none',
    cursor: 'pointer',
  },
  addButtonText: {
    color: colors.white,
    fontSize: getResponsiveValue(14, 16, 18),
    fontWeight: '600',
  },
  adminsContainer: {
    padding: getResponsiveValue(16, 20, 32),
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: getResponsiveValue(40, 60, 80),
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: getResponsiveValue(48, 60, 72),
    marginBottom: getResponsiveValue(16, 20, 24),
  },
  emptyTitle: {
    fontSize: getResponsiveValue(18, 20, 24),
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: getResponsiveValue(8, 10, 12),
  },
  emptySubtitle: {
    ...typography.body,
    color: colors.textLight,
  },
  adminsGrid: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: getResponsiveValue(16, 20, 24),
  },
  adminCard: {
    backgroundColor: colors.white,
    borderRadius: getResponsiveValue(8, 12, 16),
    padding: getResponsiveValue(16, 20, 24),
    flex: window.innerWidth >= 1024 ? '0 0 calc(33.333% - 16px)' : window.innerWidth >= 768 ? '0 0 calc(50% - 16px)' : '0 0 100%',
    minWidth: 280,
    ...globalStyles.shadow,
    border: `1px solid ${colors.border}`,
  },
  cardHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: getResponsiveValue(16, 20, 24),
  },
  adminInfo: {
    flex: 1,
  },
  adminName: {
    fontSize: getResponsiveValue(16, 18, 20),
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: 4,
  },
  adminEmail: {
    fontSize: getResponsiveValue(12, 14, 16),
    color: colors.textLight,
    marginBottom: 4,
  },
  adminRole: {
    fontSize: getResponsiveValue(12, 14, 16),
    color: colors.primary,
    fontWeight: '600',
  },
  statusBadge: {
    paddingLeft: getResponsiveValue(8, 10, 12),
    paddingRight: getResponsiveValue(8, 10, 12),
    paddingTop: getResponsiveValue(4, 5, 6),
    paddingBottom: getResponsiveValue(4, 5, 6),
    borderRadius: 16,
  },
  statusText: {
    color: colors.white,
    fontSize: getResponsiveValue(10, 11, 12),
    fontWeight: 'bold',
    textTransform: 'capitalize',
  },
  adminDetails: {
    marginBottom: getResponsiveValue(16, 20, 24),
  },
  detailRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: getResponsiveValue(4, 6, 8),
  },
  detailLabel: {
    ...typography.small,
    color: colors.textLight,
    fontWeight: '500',
  },
  detailValue: {
    ...typography.small,
    color: colors.text,
    fontWeight: '600',
  },
  cardActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: getResponsiveValue(8, 10, 12),
  },
  editButton: {
    backgroundColor: colors.primary,
    padding: getResponsiveValue(6, 8, 10),
    borderRadius: getResponsiveValue(4, 6, 8),
    border: 'none',
    cursor: 'pointer',
  },
  editButtonText: {
    color: colors.white,
    fontSize: getResponsiveValue(12, 14, 16),
    fontWeight: '600',
  },
  statusButton: {
    backgroundColor: colors.warning,
    padding: getResponsiveValue(6, 8, 10),
    borderRadius: getResponsiveValue(4, 6, 8),
    border: 'none',
    cursor: 'pointer',
  },
  statusButtonText: {
    color: colors.white,
    fontSize: getResponsiveValue(12, 14, 16),
    fontWeight: '600',
  },
  deleteButton: {
    backgroundColor: colors.danger,
    padding: getResponsiveValue(6, 8, 10),
    borderRadius: getResponsiveValue(4, 6, 8),
    border: 'none',
    cursor: 'pointer',
  },
  deleteButtonText: {
    color: colors.white,
    fontSize: getResponsiveValue(12, 14, 16),
    fontWeight: '600',
  },

  // Modal styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContainer: {
    backgroundColor: colors.white,
    borderRadius: getResponsiveValue(12, 16, 20),
    padding: getResponsiveValue(20, 24, 32),
    minWidth: getResponsiveValue(300, 400, 500),
    maxWidth: '90%',
    ...globalStyles.shadow,
  },
  modalHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: getResponsiveValue(16, 20, 24),
  },
  modalTitle: {
    fontSize: getResponsiveValue(18, 20, 24),
    fontWeight: 'bold',
    color: colors.text,
  },
  closeButton: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: 24,
    color: colors.textLight,
    cursor: 'pointer',
    width: 32,
    height: 32,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    marginBottom: getResponsiveValue(20, 24, 32),
  },
  formGroup: {
    marginBottom: getResponsiveValue(16, 20, 24),
  },
  label: {
    fontSize: getResponsiveValue(14, 16, 18),
    fontWeight: '600',
    color: colors.text,
    marginBottom: getResponsiveValue(6, 8, 10),
    display: 'block',
  },
  input: {
    width: '100%',
    padding: getResponsiveValue(10, 12, 16),
    border: `1px solid ${colors.border}`,
    borderRadius: getResponsiveValue(6, 8, 10),
    fontSize: getResponsiveValue(14, 16, 18),
    color: colors.text,
    outline: 'none',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: getResponsiveValue(10, 12, 16),
    border: `1px solid ${colors.border}`,
    borderRadius: getResponsiveValue(6, 8, 10),
    fontSize: getResponsiveValue(14, 16, 18),
    color: colors.text,
    backgroundColor: colors.white,
    cursor: 'pointer',
    outline: 'none',
    boxSizing: 'border-box',
  },
  noteText: {
    fontSize: getResponsiveValue(12, 14, 16),
    color: colors.textLight,
    fontStyle: 'italic',
  },
  modalActions: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: getResponsiveValue(10, 12, 16),
  },
  cancelButton: {
    backgroundColor: colors.gray,
    padding: getResponsiveValue(10, 12, 16),
    borderRadius: getResponsiveValue(6, 8, 10),
    border: 'none',
    cursor: 'pointer',
    color: colors.white,
    fontSize: getResponsiveValue(14, 16, 18),
    fontWeight: '600',
  },
  saveButton: {
    backgroundColor: colors.primary,
    padding: getResponsiveValue(10, 12, 16),
    borderRadius: getResponsiveValue(6, 8, 10),
    border: 'none',
    cursor: 'pointer',
    color: colors.white,
    fontSize: getResponsiveValue(14, 16, 18),
    fontWeight: '600',
  },
};

export default ManageAdminsPage;